import type { ExpressionSpecification, Map as MapboxMap } from "mapbox-gl";

import { overlayPaint, type OverlayPaint } from "./overlay-paint";

export const MAP_OVERLAY_SOURCE_ID = "sitescene-map-overlays";
export const MAP_OVERLAY_FILL_LAYER_ID = "sitescene-map-overlays-fill";
export const MAP_OVERLAY_LINE_LAYER_ID = "sitescene-map-overlays-line";

export const removeOverlayLayers = (map: MapboxMap) => {
	if (map.getLayer(MAP_OVERLAY_LINE_LAYER_ID)) {
		map.removeLayer(MAP_OVERLAY_LINE_LAYER_ID);
	}
	if (map.getLayer(MAP_OVERLAY_FILL_LAYER_ID)) {
		map.removeLayer(MAP_OVERLAY_FILL_LAYER_ID);
	}
	if (map.getSource(MAP_OVERLAY_SOURCE_ID)) {
		map.removeSource(MAP_OVERLAY_SOURCE_ID);
	}
};

const fillOpacity = (paint: OverlayPaint): ExpressionSpecification => [
	"case",
	["==", ["get", "overlayStyle"], "line"],
	0,
	["has", "fillOpacity"],
	["max", 0, ["min", 1, ["to-number", ["get", "fillOpacity"]]]],
	paint.fillOpacity,
];

const applyOverlayPaint = (map: MapboxMap, paint: OverlayPaint) => {
	map.setFilter(MAP_OVERLAY_FILL_LAYER_ID, [
		"==",
		["geometry-type"],
		"Polygon",
	]);
	map.setPaintProperty(MAP_OVERLAY_FILL_LAYER_ID, "fill-color", paint.color);
	map.setPaintProperty(
		MAP_OVERLAY_FILL_LAYER_ID,
		"fill-opacity",
		fillOpacity(paint),
	);
	map.setPaintProperty(MAP_OVERLAY_FILL_LAYER_ID, "fill-emissive-strength", 1);
	map.setPaintProperty(MAP_OVERLAY_LINE_LAYER_ID, "line-color", paint.color);
	map.setPaintProperty(
		MAP_OVERLAY_LINE_LAYER_ID,
		"line-opacity",
		paint.lineOpacity,
	);
	map.setPaintProperty(
		MAP_OVERLAY_LINE_LAYER_ID,
		"line-width",
		paint.lineWidth,
	);
	map.setPaintProperty(MAP_OVERLAY_LINE_LAYER_ID, "line-emissive-strength", 1);
	map.setLayoutProperty(MAP_OVERLAY_LINE_LAYER_ID, "line-cap", "round");
	map.setLayoutProperty(MAP_OVERLAY_LINE_LAYER_ID, "line-join", "round");
};

export const ensureOverlayLayers = (
	map: MapboxMap,
	data: GeoJSON.FeatureCollection,
	isDarkTheme: boolean,
) => {
	const paint = overlayPaint(isDarkTheme);
	const existingSource = map.getSource(MAP_OVERLAY_SOURCE_ID);
	if (existingSource && existingSource.type === "geojson") {
		existingSource.setData(data);
	} else {
		if (map.getLayer(MAP_OVERLAY_LINE_LAYER_ID)) {
			map.removeLayer(MAP_OVERLAY_LINE_LAYER_ID);
		}
		if (map.getLayer(MAP_OVERLAY_FILL_LAYER_ID)) {
			map.removeLayer(MAP_OVERLAY_FILL_LAYER_ID);
		}
		if (map.getSource(MAP_OVERLAY_SOURCE_ID)) {
			map.removeSource(MAP_OVERLAY_SOURCE_ID);
		}
		map.addSource(MAP_OVERLAY_SOURCE_ID, {
			data,
			// Use zoneId for feature-state (hover) so string ids work reliably.
			promoteId: "zoneId",
			type: "geojson",
		});
	}

	if (!map.getLayer(MAP_OVERLAY_FILL_LAYER_ID)) {
		map.addLayer({
			filter: ["==", ["geometry-type"], "Polygon"],
			id: MAP_OVERLAY_FILL_LAYER_ID,
			paint: {
				"fill-color": paint.color,
				"fill-emissive-strength": 1,
				"fill-opacity": fillOpacity(paint),
			},
			slot: "top",
			source: MAP_OVERLAY_SOURCE_ID,
			type: "fill",
		});
	}

	if (!map.getLayer(MAP_OVERLAY_LINE_LAYER_ID)) {
		map.addLayer({
			id: MAP_OVERLAY_LINE_LAYER_ID,
			layout: {
				"line-cap": "round",
				"line-join": "round",
			},
			paint: {
				"line-color": paint.color,
				"line-emissive-strength": 1,
				"line-opacity": paint.lineOpacity,
				"line-width": paint.lineWidth,
			},
			slot: "top",
			source: MAP_OVERLAY_SOURCE_ID,
			type: "line",
		});
	}

	applyOverlayPaint(map, paint);
};
